import React, {useEffect, useRef, useState} from "react";
import styles from './inputs.module.css'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronUp, faCloudArrowUp} from "@fortawesome/free-solid-svg-icons";
import ReactSlider from "react-slider";
import {useTranslation} from "react-i18next";

function TextInput({name, internalName, placeholder, form, setForm, hideName, password, email, noMargin}) {
    const [formName, setFormName] = useState(internalName);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    return (
        <div className={`form-group ${!noMargin && 'mb-3'}`}>
            { !hideName && <div className={`${styles.inputLabel} mb-1`}>{name}</div> }
            <input
                type={password ? 'password' : (email ? 'email' : 'text')}
                className={`${styles.inputBorderDefault} form-control rounded-2`}
                id={name}
                placeholder={placeholder}
                value={form[formName] ?? ''}
                onChange={(event) => setField(event.target.value)}
            />
        </div>
    )
}

function BigTextInput({name, internalName, placeholder, form, setForm}) {
    const [formName, setFormName] = useState(internalName);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    return (
        <div className="form-group mb-3">
            <div className={`${styles.inputLabel} mb-1`}>{name}</div>
            <textarea
                className={`${styles.inputBorderDefault} form-control rounded-2`}
                id={name}
                placeholder={placeholder}
                value={form[formName]}
                onChange={(event) => setField(event.target.value)}
            />
        </div>
    )
}

function SelectInput({ name, internalName, options, form, setForm, noText, hideName }) {
    const {t} = useTranslation();
    const [formName, setFormName] = useState(internalName);
    const [valueName, setValueName] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [internalUpdate, setInternalUpdate] = useState(false);

    useEffect(() => {
        if(!internalUpdate) {
            const filtered = options.filter((option) => option.id === form[formName]);

            setValueName(filtered[0]?.name);
        }
        setInternalUpdate(false);
    }, [form[formName]]);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
            const filtered = options.filter((option) => option.id === form[name]);
            setValueName(filtered[0]?.name ?? options[form[name]]?.name);
        } else {
            const filtered = options.filter((option) => option.id === form[formName]);
            setValueName(filtered[0]?.name ?? options[form[formName]]?.name);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    const handleOutsideClick = (event) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
        ) {
            setIsOpen(false);
        }
    };


    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const setField = (value) => {
        setForm((prevForm) => ({
            ...prevForm,
            [formName]: value,
        }));
        setIsOpen(false);
    };

    const setValue = (name, value) => {
        setInternalUpdate(true);
        setValueName(name);
        setField(value)
    }

    return (
        <div className={`${styles.customSelect}`} ref={dropdownRef} style={{userSelect: 'none'}}>
            {!hideName && <div className={`${styles.inputLabel}`}>{name}</div>}
            <div
                className={styles.dropdownButton}
                onClick={toggleDropdown}
            >
                <div>{valueName || (noText ? '-' : t('common.selectOption'))}</div>
                {isOpen ? <FontAwesomeIcon icon={faChevronUp}/> : <FontAwesomeIcon icon={faChevronDown}/>}
            </div>
            <div className={`${styles.dropdownOptions} ${isOpen ? styles.show : ''}`} style={{maxHeight: '200px', overflowY: 'scroll'}}>
                {options && options.map((option, index) => (
                    <div
                        key={index}
                        className={`${form[formName] === option.id ? styles.selected : ''}`}
                        onClick={() => setValue(option.name, option.id)}
                    >
                        {option.name}
                    </div>
                ))}
            </div>
        </div>
    );
}

function RadioInput({name, internalName, options, form, setForm, hideName}) {
    const [formName, setFormName] = useState(internalName);
    const [checked, setChecked] = useState(form[formName]);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
            setChecked(form[name]);
        } else {
            setChecked(form[internalName]);
            console.log('in');
            console.log(form[internalName]);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    const handleRadioChange = (index) => {
        setChecked(index);
        setField(index);
    }

    return (
        <div className="form-group mb-3">
            {!hideName && <div className={`${styles.inputLabel} mb-1`}>{name}</div>}
            <div className={styles.customRadio}>
                {options.map((option, index) => (
                    <div key={index} className={styles.radioOption}>
                        <input
                            type="radio"
                            id={`${name}-${index}`}
                            name={name}
                            checked={checked === index}
                            onChange={() => handleRadioChange(index)}
                        />
                        <label htmlFor={`${name}-${index}`}>{option}</label>
                    </div>
                ))}
            </div>
        </div>
    );
}

function FileInput({name, internalName, form, setForm}) {
    const [formName, setFormName] = useState(internalName);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
        setForm({
            ...form,
            [formName]: event.target.files[0]
        });
    };

    const handleFileClick = () => {
        fileInputRef.current.click();
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files);
        setSelectedFiles(files);
        setForm({
            ...form,
            [formName]: event.dataTransfer.files[0]
        });
    };

    return (
        <div className={styles.fileInputOutsideContainer}>
            <div
                className={`${styles.fileInputContainer} py-4`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <div
                    className={styles.fileInput}
                    onClick={handleFileClick}
                >
                    <div className={styles.uploadText}>
                        <FontAwesomeIcon icon={faCloudArrowUp} size={'2xl'} color={'#6B7280'}/>
                        <div className={'d-flex'}>
                            <div className={styles.defaultColorText}>Upload an image</div>or drag and drop
                        </div>
                        <div>PNG, JPG</div>
                    </div>
                </div>
                <input
                    type="file"
                    accept=".png,.jpg"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                {selectedFiles.length > 0 && (
                    <div className={`${styles.fileList}`}>
                        {selectedFiles.map((file, index) => (
                            <div className={styles.fileItem} key={index}>
                                <img
                                    className={styles.previewImage}
                                    src={URL.createObjectURL(file)}
                                    alt="Preview"
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

function LogoInput({name, internalName, form, setForm, current, circular}) {
    const {t} = useTranslation();
    const [formName, setFormName] = useState(internalName);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
        setForm({
            ...form,
            [formName]: event.target.files[0]
        });
    };

    const handleFileClick = () => {
        fileInputRef.current.click();
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files);
        setSelectedFiles(files);
        setForm({
            ...form,
            [formName]: event.dataTransfer.files[0]
        });
    };

    return (
        <div className={styles.fileInputOutsideContainer}>
            <div
                className={`${styles.fileInputContainer}`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <input
                    type="file"
                    accept=".png,.jpg"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                {selectedFiles.length > 0 ? (
                    <div className={`${styles.fileList}`}>
                        {selectedFiles.map((file, index) => (
                            <div
                                key={index}
                                className={styles.fileInput}
                                onClick={handleFileClick}
                            >
                                <div className={styles.uploadText}>
                                    <div className={styles.fileItem}>
                                        <img
                                            className={styles.previewImage}
                                            src={URL.createObjectURL(file)}
                                            alt="Preview"
                                        />
                                    </div>
                                    <div>
                                        <div className={`${styles.defaultColorText} d-flex ps-3`}>{t('common.addPicture')}</div>
                                        <div>[Max: 1MB]</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) :
                    <div
                        className={styles.fileInput}
                        onClick={handleFileClick}
                    >
                        <div className={styles.uploadText}>
                            <img src={`${current ? current : '/mid_cloud.png'}`} alt={'cloud'} height={'80px'} width={'80px'} style={{borderRadius: circular ? '50%' : '8px', objectFit: circular ? 'cover' : 'contain'}}/>
                            <div style={{textAlign: 'left', marginRight: '5px'}}>
                                <div className={`d-flex ps-3`}
                                     style={{
                                         color: '#4F46E5',
                                         fontWeight: '600'
                                     }}
                                >{current ? t('common.changePicture') : t('common.addPicture')}</div>
                                <div className={'ps-3'}>[Max: 1MB]</div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

function DateInput({name, internalName, form, setForm}) {
    const [formName, setFormName] = useState(internalName);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    return (
        <div className="form-group mb-3">
            <div className={`${styles.inputLabel} mb-1`}>{name}</div>
            <input
                type={'date'}
                className={`${styles.inputBorderDefault} form-control rounded-2`}
                id={name}
                value={form[formName]}
                onChange={(event) => setField(event.target.value)}
            />
        </div>
    )
}

function DateTimeInput({name, internalName, form, setForm}) {
    const [formName, setFormName] = useState(internalName);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    return (
        <div className="form-group">
            <div className={`${styles.inputLabel} mb-1`}>{name}</div>
            <input
                type={'datetime-local'}
                className={`${styles.inputBorderDefault} form-control rounded-2`}
                id={name}
                value={form[formName]}
                onChange={(event) => setField(event.target.value)}
            />
        </div>
    )
}

function SliderInput({name, internalName, form, setForm, min, max, step}) {
    const [formName, setFormName] = useState(internalName);

    useEffect(() => {
        if (!formName) {
            setFormName(name);
        }
    }, []);

    const setField = (value) => {
        setForm({
            ...form,
            [formName]: value
        });
    }

    const trackStyle = {
        height: "2px",
        background: `linear-gradient(to right, #4F46E5 ${((form[formName] - 1) / (max - 1)) * 100}%, #D3D3D3 ${(form[formName] / (max - 1)) * 100}%)`,
    };

    return (
        <div className={'mt-2 mb-4'}>
            <ReactSlider
                min={min}
                max={max}
                step={step}
                onChange={(event) => setField(event)}
                value={form[formName]}
                renderThumb={(props, state) => (
                    <div {...props} className={styles.sliderThumb} />
                )}
                renderTrack={(props, state) => (
                    <div {...props} style={trackStyle} />
                )}
            />
        </div>
    )
}

function PrimaryButton({name, onClick}) {
    return (
        <div className={styles.primaryButton} onClick={onClick}>
            <div>{name}</div>
        </div>
    );
}

function SecondaryButton({name, onClick}) {
    return (
        <div className={styles.secondaryButton} onClick={onClick}>
            <div>{name}</div>
        </div>
    );
}

function DangerButton({name, onClick}) {
    return (
        <div className={styles.dangerButton} onClick={onClick}>
            <div>{name}</div>
        </div>
    );
}

export {
    TextInput,
    BigTextInput,
    SelectInput,
    RadioInput,
    FileInput,
    DateInput,
    DateTimeInput,
    SliderInput,
    LogoInput,

    PrimaryButton,
    SecondaryButton,
    DangerButton
};
